import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Coffee, Plus, LogOut, Shield } from "lucide-react";
import { fetchMe, fetchMyTenants, logout, startGoogleLogin } from "../lib/auth";

/**
 * OwnerHome — the café owner's landing page after Google sign-in.
 * Lists tenants they own with quick links to admin dashboards.
 * If they own 0 tenants, prompts them to complete signup.
 */
const OwnerHome = () => {
    const nav = useNavigate();
    const location = useLocation();
    const [user, setUser] = useState(location.state?.user || null);
    const [tenants, setTenants] = useState([]);
    const [loading, setLoading] = useState(true);
    const [signedOut, setSignedOut] = useState(false);

    useEffect(() => {
        (async () => {
            const me = user || (await fetchMe());
            if (!me) {
                setSignedOut(true);
                return;
            }
            setUser(me);
            try {
                const ts = await fetchMyTenants();
                setTenants(ts);
            } catch {
                setTenants([]);
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const doLogout = async () => {
        await logout();
        nav("/", { replace: true });
    };

    if (signedOut) {
        return (
            <div data-testid="owner-signed-out" className="min-h-screen bg-[#F9F6F0] flex items-center justify-center px-6">
                <div className="w-full max-w-sm rounded-3xl bg-white border border-[#E8DCCB] p-8 text-center">
                    <div className="font-display font-black text-2xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <h1 className="mt-6 font-display font-black text-2xl">Café owner login</h1>
                    <p className="mt-2 text-sm text-[#8C7A72]">Sign in with the Google account you used to list your café.</p>
                    <button data-testid="owner-google-login" onClick={() => startGoogleLogin("/owner")} className="mt-6 w-full rounded-full bg-[#2C1A12] text-white py-3 font-display font-bold active:scale-95 transition-transform">
                        Continue with Google
                    </button>
                    <p className="mt-4 text-xs text-[#8C7A72]">
                        Signed up with a PIN instead? Go to <span className="font-bold">coffeein7.com/your-cafe/admin</span>.
                    </p>
                    <a href="/signup" data-testid="owner-signup-link" className="mt-4 inline-block text-sm font-display font-bold text-[#FF4F18]">
                        New here? List your café →
                    </a>
                </div>
            </div>
        );
    }

    if (loading || !user) {
        return (
            <div className="min-h-screen bg-[#F9F6F0] flex items-center justify-center text-[#8C7A72]">
                Loading your cafés…
            </div>
        );
    }

    const firstName = (user.name || user.email).split(" ")[0].split("@")[0];

    return (
        <div className="min-h-screen bg-[#F9F6F0]">
            <header className="border-b border-[#E8DCCB]/60 bg-white/80 backdrop-blur sticky top-0 z-20">
                <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="font-display font-black text-2xl">
                        Coffee<span className="text-[#FF4F18]">in7.</span>
                    </div>
                    <button
                        data-testid="owner-logout"
                        onClick={doLogout}
                        className="flex items-center gap-2 text-sm font-semibold text-[#8C7A72] hover:text-[#FF4F18]"
                    >
                        <LogOut className="h-4 w-4" strokeWidth={2.5} />
                        Sign out
                    </button>
                </div>
            </header>

            <div className="max-w-4xl mx-auto px-6 py-12">
                <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
                    <div className="flex items-center gap-4">
                        {user.picture && (
                            <img
                                src={user.picture}
                                alt=""
                                className="h-14 w-14 rounded-full ring-2 ring-[#E8DCCB]"
                            />
                        )}
                        <div>
                            <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#FF4F18]">
                                Owner console
                            </div>
                            <h1 className="mt-1 font-display font-black text-4xl leading-tight">
                                Hi {firstName}
                            </h1>
                            <div className="text-sm text-[#8C7A72] mt-1">{user.email}</div>
                        </div>
                    </div>
                </motion.div>

                <div className="mt-10 flex items-baseline justify-between">
                    <div className="text-xs uppercase tracking-[0.3em] font-bold text-[#8C7A72]">
                        Your cafés · {tenants.length}
                    </div>
                    <button
                        data-testid="owner-add-cafe"
                        onClick={() => nav("/signup", { state: { user } })}
                        className="inline-flex items-center gap-1.5 rounded-full bg-[#2C1A12] text-white px-4 py-2 text-xs font-display font-bold active:scale-95"
                    >
                        <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        Add another café
                    </button>
                </div>

                {tenants.length === 0 ? (
                    <div className="mt-6 rounded-3xl bg-white border-2 border-dashed border-[#E8DCCB] p-10 text-center">
                        <Coffee className="h-10 w-10 text-[#FF4F18] mx-auto" strokeWidth={2} />
                        <div className="mt-4 font-display font-black text-2xl">
                            You don't have a café yet
                        </div>
                        <p className="mt-2 text-sm text-[#8C7A72] max-w-md mx-auto">
                            Create your first café now — it takes about 90 seconds.
                        </p>
                        <button
                            data-testid="owner-create-first"
                            onClick={() => nav("/signup", { state: { user } })}
                            className="mt-6 px-8 py-4 rounded-full bg-[#FF4F18] text-white font-display font-bold inline-flex items-center gap-2 active:scale-95 shadow-[0_12px_32px_rgba(255,79,24,0.32)]"
                        >
                            Create my café
                            <ArrowRight className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </div>
                ) : (
                    <div className="mt-6 grid gap-3">
                        {tenants.map((t) => (
                            <button
                                key={t.slug}
                                data-testid={`owner-cafe-${t.slug}`}
                                onClick={() => nav(`/${t.slug}/admin/dashboard`)}
                                className="text-left rounded-3xl bg-white border border-[#E8DCCB] p-5 hover:border-[#FF4F18] hover:shadow-[0_16px_40px_rgba(255,79,24,0.08)] transition-all active:scale-[0.99] flex items-center gap-4"
                            >
                                <div className="h-12 w-12 rounded-2xl bg-[#FF4F18]/10 flex items-center justify-center shrink-0">
                                    <Shield className="h-5 w-5 text-[#FF4F18]" strokeWidth={2.5} />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="font-display font-black text-xl leading-tight">
                                        {t.name}
                                    </div>
                                    <div className="text-xs text-[#8C7A72] mt-0.5">
                                        <span className="font-mono">/{t.slug}</span>
                                        {t.plan && (
                                            <>
                                                <span> · </span>
                                                <span className="uppercase tracking-widest font-bold text-[#FF4F18]">
                                                    {t.plan}
                                                </span>
                                            </>
                                        )}
                                        {t.status === "suspended" && (
                                            <span className="ml-2 text-[#FF4F18] font-bold">
                                                · Suspended
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <ArrowRight className="h-5 w-5 text-[#8C7A72] shrink-0" strokeWidth={2.5} />
                            </button>
                        ))}
                    </div>
                )}

                <div className="mt-10 text-center text-xs text-[#8C7A72]">
                    Signed in with Google · Session lasts 7 days
                </div>
            </div>
        </div>
    );
};

export default OwnerHome;
